'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { criarEscritorio } from '@/features/escritorio/store/crud/actions';
import { applyCpfCnpjMask, isValidCpfCnpj } from '@/shared/utils/cnpj';
import { isCnpj, isCpf, normalizeCpfCnpj } from '@/shared/utils/documentValidation';
import { useAppDispatch } from '@/store/hooks';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}
export default function CreateEscritorioModal({
  isOpen,
  onClose,
  onSuccess
}: Props) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState({
    nomeFantasia: '',
    documento: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [documentType, setDocumentType] = useState<'cpf' | 'cnpj' | null>(null);
  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const maskedValue = applyCpfCnpjMask(rawValue);
    setFormData(prev => ({
      ...prev,
      documento: maskedValue
    }));
    if (isCpf(rawValue)) {
      setDocumentType('cpf');
    } else if (isCnpj(rawValue)) {
      setDocumentType('cnpj');
    } else {
      setDocumentType(null);
    }
  };
  const handleSubmit = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    setError('');
    const cleanedDocumento = normalizeCpfCnpj(formData.documento);
    if (cleanedDocumento && !isValidCpfCnpj(cleanedDocumento)) {
      setError('CPF/CNPJ inválido');
      return;
    }
    setLoading(true);
    try {
      await dispatch(criarEscritorio({
        nomeFantasia: formData.nomeFantasia,
        documento: cleanedDocumento,
        tipo: documentType === 'cpf' ? 'PESSOAL' : 'EMPRESARIAL' // Assuming 'EMPRESARIAL' for CNPJ or null
      })).unwrap();
      if (onSuccess) {
        onSuccess();
      }
      router.refresh();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Criar Escritório</h2>
          <p className="mt-1 text-gray-600">Configure seu escritório e comece a trabalhar</p>
        </div>

        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="documento" className="mb-1 block text-sm font-medium text-gray-700">
              CPF/CNPJ <span className="text-red-500">*</span>
            </label>
            <input id="documento" type="text" required value={formData.documento} onChange={handleDocumentChange} maxLength={documentType === 'cpf' ? 14 : 18} // Max length for masked CPF or CNPJ
          className="w-full rounded-lg border border-gray-300 px-4 py-2 transition outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500" placeholder="000.000.000-00 ou 00.000.000/0000-00" />
          </div>

          <div>
            <label htmlFor="nomeFantasia" className="mb-1 block text-sm font-medium text-gray-700">
              Nome da Organização <span className="text-red-500">*</span>
            </label>
            <input id="nomeFantasia" type="text" required value={formData.nomeFantasia} onChange={e_1 => setFormData({
            ...formData,
            nomeFantasia: e_1.target.value
          })} className="w-full rounded-lg border border-gray-300 px-4 py-2 transition outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500" placeholder={documentType === 'cpf' ? 'Nome Completo' : 'Razão Social'} />
          </div>

          <div className="mt-6 flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-gray-300 px-4 py-2 font-medium text-gray-700 transition hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={loading} className="bg-primary hover:bg-brand-700 flex-1 rounded-lg px-4 py-2 font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-50">
              {loading ? 'Criando...' : 'Criar Escritório'}
            </button>
          </div>
        </form>

        <div className="mt-4 rounded-lg bg-blue-50 p-3">
          <p className="text-xs text-blue-800">
            <strong>Importante:</strong> Você será automaticamente o proprietário do escritório e
            poderá convidar outros membros.
          </p>
        </div>
      </div>
    </div>;
}